// Shared consent state for the whole app. Mounted once via `wrapRootElement`
// (gatsby-shared.js), so any component in the tree (banner, tracker injector,
// footer "manage cookies" link) can read/update the decision.
//
// SSR-safe: on the first render `mounted` is false and we expose consent=null /
// showBanner=false, which matches the server output (nothing tracker- or
// banner-related is emitted). After hydration the useEffect reads the cookie and
// the real state kicks in — no hydration mismatch.

import React, { createContext, useContext, useEffect, useState } from "react"
import {
  CONSENT_GRANTED,
  CONSENT_DENIED,
  readConsent,
  writeConsent,
  clearTrackingCookies,
} from "./consent-cookie"
import { updateConsentMode } from "./consent-mode"

const ConsentContext = createContext({
  consent: null,
  mounted: false,
  showBanner: false,
  accept: () => {},
  decline: () => {},
  reopen: () => {},
})

export const useConsent = () => useContext(ConsentContext)

export const ConsentProvider = ({ children }) => {
  const [mounted, setMounted] = useState(false)
  const [consent, setConsent] = useState(null)
  // Lets the visitor reopen the banner from the footer to change their mind,
  // even after a choice was already stored.
  const [reopened, setReopened] = useState(false)

  useEffect(() => {
    const stored = readConsent()
    // No choice yet: proactively clear any tracking cookies a returning visitor
    // still carries from before this banner existed (they're inert now — no
    // script reads them — but Loi 25 disfavours keeping them without consent).
    if (stored === null) clearTrackingCookies()
    setConsent(stored)
    setMounted(true)
  }, [])

  const accept = () => {
    updateConsentMode(true)
    writeConsent(CONSENT_GRANTED)
    setConsent(CONSENT_GRANTED)
    setReopened(false)
  }

  const decline = () => {
    // Explicit "denied" update (not just the default) so reopening after a prior
    // "accept" correctly downgrades the live signals.
    updateConsentMode(false)
    writeConsent(CONSENT_DENIED)
    clearTrackingCookies()
    setConsent(CONSENT_DENIED)
    setReopened(false)
  }

  const reopen = () => setReopened(true)

  // Show the banner only after hydration, when no choice has been made yet OR
  // the visitor explicitly reopened it.
  const showBanner = mounted && (consent === null || reopened)

  return (
    <ConsentContext.Provider
      value={{ consent, mounted, showBanner, accept, decline, reopen }}
    >
      {children}
    </ConsentContext.Provider>
  )
}
